import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Download, Settings, Zap, Rocket, CheckCircle, ArrowRight, Play, Clock, Cpu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SHOPIFY_INSTALL_URL } from '@/components/Navbar';

const HIW_HERO_IMG = '/assets/How-it-works.webp';
const HIW_SETUP_IMG = '/assets/how-it-works-setup.jpg';

const HowItWorksPage = () => {
  const steps = [
    {
      number: '01',
      icon: Download,
      title: 'Install AI Agent',
      description: 'One-click installation from Shopify App Store. No coding, no complex setup.',
      details: ['One-click installation', 'No technical skills needed', 'Automatic authentication'],
      color: 'bg-teal-50 border-teal-200',
      iconBg: 'bg-teal-600'
    },
    {
      number: '02',
      icon: Settings,
      title: 'Train Your Model',
      description: 'AI automatically learns your product catalog, pricing, and brand voice.',
      details: ['Real-time catalog sync', 'Brand voice learning', 'Product data indexing'],
      color: 'bg-cyan-50 border-cyan-200',
      iconBg: 'bg-cyan-600'
    },
    {
      number: '03',
      icon: Zap,
      title: 'Activate AI Sales',
      description: 'Configure AI personality and enable intelligent sales automation.',
      details: ['Custom AI personality', 'Sales trigger setup', 'Automation rules'],
      color: 'bg-emerald-50 border-emerald-200',
      iconBg: 'bg-emerald-600'
    },
    {
      number: '04',
      icon: Rocket,
      title: 'Scale Revenue',
      description: 'Watch AI handle conversations and drive sales 24/7 on autopilot.',
      details: ['Instant activation', 'Real-time analytics', '24/7 automated sales'],
      color: 'bg-green-50 border-green-200',
      iconBg: 'bg-green-600'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFDFD]" data-testid="how-it-works-page">
      <Navbar />
      
      <main className="pt-24 sm:pt-32 pb-16 sm:pb-24">
        {/* Hero with Image */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 mb-16 sm:mb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-3 sm:px-4 py-2 mb-4 sm:mb-6 shadow-sm">
                <CheckCircle className="w-4 h-4 text-teal-600" />
                <span className="text-xs sm:text-sm font-bold text-teal-700">Simple Deployment</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-4 sm:mb-6 leading-tight">
                Deploy AI in Minutes
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-slate-600 max-w-xl mb-6 sm:mb-8 leading-relaxed">
                From installation to AI-powered sales in under 5 minutes. No coding required.
              </p>
              <div className="inline-flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-full px-4 sm:px-6 py-2 sm:py-3">
                <Clock className="w-5 sm:w-6 h-5 sm:h-6 text-emerald-600" />
                <span className="text-emerald-700 font-bold text-sm sm:text-lg">Average setup: Under 5 minutes</span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <img 
                src={HIW_HERO_IMG}
                alt="Mobile e-commerce app with product recommendations"
                className="rounded-2xl sm:rounded-3xl shadow-xl w-full h-[260px] sm:h-[340px] object-cover border-2 border-slate-100"
                data-testid="hiw-hero-image"
              />
              <motion.div 
                className="absolute -bottom-4 -right-3 sm:-right-5 bg-white rounded-xl shadow-lg p-3 sm:p-4 border border-emerald-200"
                animate={{ y: [0, -5, 0] }}
                transition={{ repeat: Infinity, duration: 3 }}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm font-bold text-slate-900">No coding needed</span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Steps */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 mb-16 sm:mb-24">
          <div className="space-y-6 sm:space-y-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                className={`bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 border-2 ${step.color} shadow-sm hover:shadow-lg transition-all`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`step-card-${index}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-center">
                  <div>
                    <div className="flex items-center gap-4 sm:gap-5 mb-4 sm:mb-6">
                      <span className="text-5xl sm:text-6xl md:text-7xl font-extrabold text-slate-200">{step.number}</span>
                      <div className={`w-14 sm:w-16 h-14 sm:h-16 rounded-xl sm:rounded-2xl ${step.iconBg} flex items-center justify-center shadow-lg`}>
                        <step.icon className="w-7 sm:w-8 h-7 sm:h-8 text-white" />
                      </div>
                    </div>
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-slate-900 mb-3 sm:mb-4">{step.title}</h2>
                    <p className="text-base sm:text-lg text-slate-600 leading-relaxed">{step.description}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-slate-200">
                    <ul className="space-y-3 sm:space-y-4">
                      {step.details.map((detail) => (
                        <li key={detail} className="flex items-center gap-3 sm:gap-4 text-slate-700">
                          <CheckCircle className="w-5 sm:w-6 h-5 sm:h-6 text-teal-500 flex-shrink-0" />
                          <span className="text-sm sm:text-lg font-medium">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Showcase Image */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 mb-16 sm:mb-24">
          <motion.div 
            className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <img 
              src={HIW_SETUP_IMG}
              alt="Simple setup process for AI e-commerce agent"
              className="w-full h-[200px] sm:h-[280px] object-cover"
              data-testid="hiw-showcase-image"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/70 to-transparent flex items-center">
              <div className="p-6 sm:p-10 max-w-lg">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-white mb-2 sm:mb-3">Simple. Fast. Powerful.</h3>
                <p className="text-sm sm:text-base text-emerald-100">Your AI agent starts learning and selling within minutes of installation.</p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-teal-600 to-emerald-600 py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-4 sm:mb-6">
                Ready to Deploy AI?
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-teal-100 mb-8 sm:mb-10 px-4">
                Install Arismate and start converting visitors into customers today.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
                <a href={SHOPIFY_INSTALL_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto bg-white text-teal-700 hover:bg-teal-50 group font-bold text-sm sm:text-base px-6 sm:px-8 py-5 sm:py-6 shadow-xl" data-testid="hiw-install-btn">
                    <Cpu className="w-5 h-5 mr-2" />
                    Install on Shopify
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white/40 text-white hover:bg-white/10 font-bold text-sm sm:text-base px-6 sm:px-8 py-5 sm:py-6" data-testid="hiw-contact-btn">
                    <Play className="w-5 h-5 mr-2" />
                    Contact Us
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HowItWorksPage;
